interface Logo {
  url: string;
  width: number;
  height: number;
}

interface OGImage {
  url: string;
  width: number;
  height: number;
  alt: string;
  type: string;
}

interface Social {
  linkedin: string;
  x: string;
  github: string;
  xHandle: string;
  facebook: string;
}

interface Contact {
  email: string;
  phone: string;
  type: string;
}

interface Address {
  street: string;
  locality: string;
  region: string;
  postalCode: string;
  country: string;
}

interface InstantArticlesConfig {
  markupUrl: string;
  markupUrlDev: string;
  rulesUrl: string;
  rulesUrlDev: string;
}

interface Organization {
  name: string;
  legalName: string;
  registrationNumber: string;
  fullAddress: string;
  description: string;
  keywords: string[];
  social: Social;
  contact: Contact;
  address: {
    official: Address;
    office: Address;
  };
  boardMembers: string[];
}

interface Search {
  path: string;
  queryInput: string;
}

interface Pricing {
  currency: string;
}

interface Policies {
  privacyPolicy: {
    effectiveDate: string;
    version: string;
  };
  termsOfService: {
    effectiveDate: string;
    version: string;
  };
}

interface SiteConfig {
  baseUrl: string;
  assetUrl: string;
  logo: Logo;
  ogImage: OGImage;
  organization: Organization;
  search: Search;
  pricing: Pricing;
  performance: {
    preconnectDomains: string[];
  };
  policies: Policies;
  googleSiteVerification: string;
  FacebookAppID: string;
  publishedTime: string;
  modifiedTime: string;
  modifiedTimeEpoch: string;
  authors: string;
  instantArticles: InstantArticlesConfig;
}

const isValidUrl = (urlString: string): boolean => {
  try {
    new URL(urlString);
    return true;
  } catch {
    return false;
  }
};

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');
const stripLeadingSlash = (path: string): string => path.replace(/^\/+/, '');
const joinUrl = (base: string, path: string): string => {
  const normalizedBase = `${stripTrailingSlash(base)}/`;
  const sanitizedPath = stripLeadingSlash(path);
  if (!sanitizedPath) {
    return stripTrailingSlash(normalizedBase);
  }
  try {
    return new URL(sanitizedPath, normalizedBase).toString();
  } catch {
    return `${stripTrailingSlash(base)}/${sanitizedPath}`;
  }
};

const defaultCanonicalUrl = stripTrailingSlash('https://perxify.com');
const defaultAssetUrl = stripTrailingSlash('https://perxify.com');

const getValidUrl = (url: string | undefined, fallback: string): string => {
  if (!url) {
    return stripTrailingSlash(fallback);
  }

  if (!isValidUrl(url)) {
    return stripTrailingSlash(fallback);
  }

  return stripTrailingSlash(url);
};

const getOptionalUrl = (url: string | undefined): string => {
  if (!url) {
    return '';
  }

  if (!isValidUrl(url)) {
    return '';
  }

  return stripTrailingSlash(url);
};

const resolvedBaseUrl = getValidUrl(process.env.NEXT_PUBLIC_BASE_URL, defaultCanonicalUrl);
const resolvedAssetUrl = getValidUrl(process.env.NEXT_PUBLIC_ASSET_URL, defaultAssetUrl);
const buildAssetPath = (path: string): string => joinUrl(resolvedAssetUrl, path);
const resolvedInstantArticles: InstantArticlesConfig = {
  markupUrl: getOptionalUrl(process.env.NEXT_PUBLIC_IA_MARKUP_URL),
  markupUrlDev: getOptionalUrl(process.env.NEXT_PUBLIC_IA_MARKUP_URL_DEV),
  rulesUrl: getOptionalUrl(process.env.NEXT_PUBLIC_IA_RULES_URL),
  rulesUrlDev: getOptionalUrl(process.env.NEXT_PUBLIC_IA_RULES_URL_DEV),
};

const siteConfig: SiteConfig = {
  baseUrl: resolvedBaseUrl,
  
  assetUrl: resolvedAssetUrl,
  
  logo: {
    url: buildAssetPath('images/logo.png'),
    width: 112,
    height: 112,
  },

  ogImage: {
    url: buildAssetPath('images/og-image.jpg'),
    width: 1200,
    height: 630,
    alt: 'Perxify AI-Powered Commerce Cloud Platform',
    type: 'image/jpeg',
  },

  FacebookAppID: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || '776787011364683',
  
  publishedTime: '2025-07-14T00:00:00Z',
  modifiedTime: '2025-10-27T13:07:49Z',
  modifiedTimeEpoch: '1761570469',
  authors: 'Perxify Team',

  organization: {
    name: 'Perxify',
    legalName: 'Alder Solutions OÜ',
    registrationNumber: '17099988',
    fullAddress: 'Liivaoja tn 1b-25, Tallinn 10155, Estonia',
    description: 'Launch and scale your e-commerce business with Perxify\'s AI-powered Commerce Cloud. Build stores, marketplaces, loyalty programs with blockchain integration.',
    keywords: [
      'AI commerce platform',
      'e-commerce cloud',
      'marketplace builder',
      'loyalty program software',
      'blockchain commerce',
      'multi-vendor marketplace',
      'commerce analytics'
    ],
    social: {
      linkedin: 'https://linkedin.com/company/perxify',
      x: 'https://x.com/perxifycom',
      github: 'https://github.com/perxify',
      facebook: 'https://www.facebook.com/Perxify',
      xHandle: '@perxifycom',
    },
    contact: {
      email: 'hello@perxify.com',
      phone: '+37256218861',
      type: 'customer service',
    },
    address: {
      official: {
        street: 'Liivaoja 1B-25',
        locality: 'Tallinn',
        region: 'Harjumaa',
        postalCode: '10155',
        country: 'Estonia',
      },
      office: {
        street: 'Jakobsoni 3-5',
        locality: 'Tallinn',
        region: 'Harjumaa',
        postalCode: '10128',
        country: 'Estonia',
      },
    },
    boardMembers: ['Sander Gansen', 'Alvar Laigna'],
  },
  search: {
    path: '/search?q={search_term_string}',
    queryInput: 'required name=search_term_string',
  },
  pricing: {
    currency: 'EUR',
  },
  performance: {
    preconnectDomains: [
      'https://www.googletagmanager.com',
      'https://www.google-analytics.com',
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
    ],
  },
  policies: {
    privacyPolicy: {
      effectiveDate: 'June 17, 2025',
      version: '1.0',
    },
    termsOfService: {
      effectiveDate: 'June 17, 2025',
      version: '1.0',
    },
  },
  googleSiteVerification: '',
  instantArticles: resolvedInstantArticles,
};

if (process.env.NODE_ENV === 'development') {
  if (!process.env.NEXT_PUBLIC_BASE_URL) {
    console.warn(
      'NEXT_PUBLIC_BASE_URL is not set. Using default canonical URL for development.'
    );
  }
  if (!process.env.NEXT_PUBLIC_ASSET_URL) {
    console.warn(
      'NEXT_PUBLIC_ASSET_URL is not set. Using Vercel URL for assets.'
    );
  }
  if (!process.env.NEXT_PUBLIC_FACEBOOK_APP_ID) {
    console.warn(
      'NEXT_PUBLIC_FACEBOOK_APP_ID is not set. Using default Facebook App ID.'
    );
  }
}

export default siteConfig;

export const getAssetPath = (path: string): string => buildAssetPath(path);
